/**
 * The Team queue's Status filter: the queue's five groups plus Archived, which
 * otherwise only a search prints. Chosen statuses ride the address as
 * `?status=needs,stop`, always in the queue's order; none chosen is the queue
 * as it is. Each status draws the stage pie of a ticket standing in it, so the
 * menu speaks the rows' own glyphs.
 */
import type { WorkPipelineStage } from "@yagni/shared";
import type { StagePieMode } from "../ui/stagePie.js";
import { QUEUE_GROUP_LABEL, QUEUE_GROUPS, type QueueGroup } from "./queueGroups.js";

export type StatusFilter = QueueGroup | "archived";

export const STATUS_FILTERS: readonly StatusFilter[] = [...QUEUE_GROUPS, "archived"];

export const STATUS_FILTER_LABEL: Record<StatusFilter, string> = {
  ...QUEUE_GROUP_LABEL,
  archived: "Archived",
};

/** The pie a ticket in each status draws: a plan waiting on you, a build an agent is on, a shipped one. */
export const STATUS_FILTER_GLYPH: Record<
  StatusFilter,
  { stage: WorkPipelineStage; mode: StagePieMode }
> = {
  needs: { stage: "plan", mode: "wait" },
  ready: { stage: "review", mode: "wait" },
  live: { stage: "build", mode: "live" },
  stop: { stage: "build", mode: "stop" },
  done: { stage: "done", mode: "passed" },
  archived: { stage: "proposed", mode: "canceled" },
};

/** `?status=` read back: the known statuses, once each, in the queue's order. */
export function parseStatusParam(raw: string | null): StatusFilter[] {
  if (!raw) return [];
  const words = new Set(raw.split(",").map((word) => word.trim()));
  return STATUS_FILTERS.filter((status) => words.has(status));
}

/** The `?status=` value for the chosen statuses, or null when none is chosen. */
export function statusParamOf(chosen: readonly StatusFilter[]): string | null {
  const ordered = STATUS_FILTERS.filter((status) => chosen.includes(status));
  return ordered.length > 0 ? ordered.join(",") : null;
}

/** What the trigger says: "Any", one or two names, or "3 statuses". */
export function statusFilterWords(chosen: readonly StatusFilter[]): string {
  if (chosen.length === 0) return "Any";
  if (chosen.length > 2) return `${chosen.length} statuses`;
  return STATUS_FILTERS.filter((status) => chosen.includes(status))
    .map((status) => STATUS_FILTER_LABEL[status])
    .join(", ");
}
